import { useState } from "react";
import { Plus, Trash2, Send, FileText, Loader2, ExternalLink } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";

const euro = (n) => `${Number(n || 0).toFixed(2)} €`;

export function QuoteModal({ appt, onSaved }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tva, setTva] = useState(appt.quote?.tva_rate ?? 21);
  const [notes, setNotes] = useState(appt.quote?.notes ?? "");
  const [items, setItems] = useState(
    appt.quote?.items?.length
      ? appt.quote.items.map((i) => ({ ...i }))
      : [{ label: "", category: "piece", qty: 1, unit_price: 0 }]
  );

  const setItem = (i, k, v) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addRow = () => setItems((arr) => [...arr, { label: "", category: "piece", qty: 1, unit_price: 0 }]);
  const delRow = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));

  const subtotal = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);
  const total = subtotal * (1 + (Number(tva) || 0) / 100);

  const send = async () => {
    const clean = items.filter((it) => it.label.trim());
    if (clean.length === 0) { toast.error(t("admin.quote_empty")); return; }
    setSaving(true);
    try {
      await api.patch(`/appointments/${appt.id}/quote`, {
        items: clean.map((it) => ({ ...it, qty: Number(it.qty) || 0, unit_price: Number(it.unit_price) || 0 })),
        tva_rate: Number(tva) || 0,
        notes,
      });
      toast.success(t("admin.quote_sent"));
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const hasQuote = !!appt.quote;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={`quote-btn-${appt.id}`}
          className={`inline-flex items-center gap-1.5 px-3 h-10 border text-xs font-mono-tech uppercase tracking-wider transition-colors ${
            hasQuote ? "border-[#00E5FF]/50 text-[#00E5FF] hover:bg-[#00E5FF]/10" : "border-white/15 text-zinc-300 hover:border-[#E60000] hover:text-[#E60000]"
          }`}
        >
          <FileText className="w-3.5 h-3.5" /> {t("admin.quote_btn")}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0C0C0E] border border-white/10 text-white rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-900 uppercase tracking-tight text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#E60000]" /> {t("admin.quote_title")} · {appt.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-2 mt-2">
          <div className="hidden sm:grid grid-cols-12 gap-2 font-mono-tech text-[10px] uppercase tracking-wider text-zinc-600 px-1">
            <span className="col-span-5">{t("admin.quote_label")}</span>
            <span className="col-span-3">{t("admin.quote_cat")}</span>
            <span className="col-span-1 text-center">{t("admin.quote_qty")}</span>
            <span className="col-span-2 text-right">{t("admin.quote_price")}</span>
            <span className="col-span-1" />
          </div>
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`quote-row-${i}`}>
              <Input value={it.label} onChange={(e) => setItem(i, "label", e.target.value)} placeholder={t("admin.quote_label")} data-testid={`quote-item-label-${i}`} className="col-span-12 sm:col-span-5 bg-[#050505] border-white/10 rounded-none h-10 text-white text-sm placeholder:text-zinc-600 focus-visible:border-[#00E5FF] focus-visible:ring-0" />
              <select value={it.category} onChange={(e) => setItem(i, "category", e.target.value)} data-testid={`quote-item-cat-${i}`} className="col-span-5 sm:col-span-3 bg-[#050505] border border-white/10 rounded-none h-10 text-white text-sm px-2 focus:border-[#00E5FF] outline-none">
                <option value="piece">{t("admin.quote_piece")}</option>
                <option value="main">{t("admin.quote_main")}</option>
              </select>
              <Input type="number" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} data-testid={`quote-item-qty-${i}`} className="col-span-3 sm:col-span-1 bg-[#050505] border-white/10 rounded-none h-10 text-white text-sm text-center px-1 focus-visible:border-[#00E5FF] focus-visible:ring-0" />
              <Input type="number" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} data-testid={`quote-item-price-${i}`} className="col-span-3 sm:col-span-2 bg-[#050505] border-white/10 rounded-none h-10 text-white text-sm text-right px-2 focus-visible:border-[#00E5FF] focus-visible:ring-0" />
              <button onClick={() => delRow(i)} data-testid={`quote-del-${i}`} className="col-span-1 h-10 flex items-center justify-center text-zinc-500 hover:text-[#E60000] transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={addRow} data-testid="quote-add-row" className="inline-flex items-center gap-1.5 mt-1 text-[#00E5FF] font-mono-tech text-xs uppercase tracking-wider hover:text-white transition-colors">
            <Plus className="w-3.5 h-3.5" /> {t("admin.quote_add")}
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-600 block mb-1">{t("admin.quote_tva")}</label>
            <Input type="number" value={tva} onChange={(e) => setTva(e.target.value)} data-testid="quote-tva" className="bg-[#050505] border-white/10 rounded-none h-10 text-white text-sm focus-visible:border-[#00E5FF] focus-visible:ring-0" />
          </div>
          <div className="flex flex-col justify-end font-mono-tech text-sm">
            <div className="flex justify-between text-zinc-400"><span>{t("admin.quote_subtotal")}</span><span data-testid="quote-subtotal">{euro(subtotal)}</span></div>
            <div className="flex justify-between text-white text-lg font-700 mt-1"><span>{t("admin.quote_total")}</span><span className="text-[#00E5FF]" data-testid="quote-total">{euro(total)}</span></div>
          </div>
        </div>

        <div className="mt-3">
          <label className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-600 block mb-1">{t("admin.quote_notes")}</label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="quote-notes" className="bg-[#050505] border-white/10 rounded-none text-white text-sm focus-visible:border-[#00E5FF] focus-visible:ring-0 min-h-[70px]" />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mt-5">
          {hasQuote && (
            <a href={`${window.location.origin}/devis/${appt.quote_token}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-[#00E5FF] font-mono-tech text-xs uppercase tracking-wider transition-colors">
              <ExternalLink className="w-3.5 h-3.5" /> {t("admin.quote_current")}
            </a>
          )}
          <button onClick={send} disabled={saving} data-testid="quote-send" className="ml-auto inline-flex items-center gap-2 px-6 py-3 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {t("admin.quote_send")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
